import React, { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Divider, TextField, MenuItem, Checkbox, FormControlLabel,
  Switch, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  Table, TableHead, TableBody, TableRow, TableCell, Paper, CircularProgress,
} from "@material-ui/core";
import { useSelector, useDispatch } from "react-redux";
import { WatchActions } from "../../state";

/**
 * Super Admin → Mass Update. Sets warranty_id and/or papers_included on every
 * item_site_fields row for a brand (current site scope), optionally filtered by
 * status and a created-since date. Backend enforces super_admin.
 */
const SITE_NAMES = { 0: "PT", 1: "TWS", 2: "TPC" };
const siteLabel = (id) => SITE_NAMES[id] ?? `site ${id}`;

function _MassUpdatePage() {
  const dispatch = useDispatch();
  const siteId = useSelector((state) => state.scopes.site_id);
  const { warranty: warrantyList = [], status: statusList = [] } = useSelector(
    (state) => state.watches.watchLists || {},
  );

  const [brands, setBrands] = useState([]);
  const [brandId, setBrandId] = useState("");
  const [statuses, setStatuses] = useState([]); // [] = all
  const [createdSince, setCreatedSince] = useState("");
  const [modifyWarranty, setModifyWarranty] = useState(false);
  const [warrantyId, setWarrantyId] = useState("");
  const [modifyPapers, setModifyPapers] = useState(false);
  const [papersIncluded, setPapersIncluded] = useState(false);
  const [count, setCount] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);

  // Load brands + the shared lists (warranty/status) once.
  useEffect(() => {
    if (siteId === undefined) return;
    dispatch(WatchActions.getAllLists());
    (async () => {
      const data = await dispatch(WatchActions.getAttribute("brand", null, siteId));
      setBrands(Array.isArray(data) ? data : []);
    })();
  }, [dispatch, siteId]);

  const brandName =
    brands.find((b) => String(b.brand_id) === String(brandId))?.brand || brandId;

  // Live preview count (dry run) whenever the filters change.
  const runDryRun = useCallback(async () => {
    if (!brandId || siteId === undefined) { setCount(null); return; }
    const data = await dispatch(
      WatchActions.massUpdate(siteId, {
        brand_id: Number(brandId),
        statuses: statuses.map(Number),
        created_since: createdSince || null,
        dry_run: true,
      }),
    );
    setCount(data && typeof data.count === "number" ? data.count : null);
  }, [dispatch, siteId, brandId, statuses, createdSince]);

  useEffect(() => { runDryRun(); }, [runDryRun]);

  const canGo = brandId && (modifyWarranty || modifyPapers);

  const doUpdate = async () => {
    setConfirmOpen(false);
    setLoading(true);
    const data = await dispatch(
      WatchActions.massUpdate(siteId, {
        brand_id: Number(brandId),
        statuses: statuses.map(Number),
        created_since: createdSince || null,
        modify_warranty: modifyWarranty,
        warranty_id: modifyWarranty ? (warrantyId ? Number(warrantyId) : null) : undefined,
        modify_papers: modifyPapers,
        papers_included: modifyPapers ? !!papersIncluded : undefined,
        dry_run: false,
      }),
    );
    setLoading(false);
    if (data && typeof data.updated === "number") {
      setResults((prev) => [
        {
          time: new Date().toLocaleString(),
          brand: brandName,
          site: siteId,
          count: data.updated,
          changes: data.changes || {},
        },
        ...prev,
      ]);
      runDryRun();
    }
  };

  const changesSummary = () => {
    const parts = [];
    if (modifyWarranty) {
      const w = warrantyList.find((x) => String(x.warranty_id) === String(warrantyId));
      parts.push(`Warranty → ${warrantyId ? (w ? w.warranty : warrantyId) : "(none)"}`);
    }
    if (modifyPapers) parts.push(`API (papers) → ${papersIncluded ? "Yes" : "No"}`);
    return parts.join("  ·  ");
  };

  return (
    <Box p={4} style={{ maxWidth: 1100, margin: "0 auto" }}>
      <Typography variant="h5" gutterBottom>Mass Warranty/API Update</Typography>
      <Typography variant="body2" color="textSecondary">
        Updates all matching items for a brand on the current site scope ({siteLabel(siteId)}).
      </Typography>
      <Divider style={{ margin: "16px 0 24px" }} />

      <Box display="flex" flexWrap="wrap" style={{ gap: 20 }}>
        <TextField
          select label="Brand" value={brandId} style={{ width: 260 }} variant="outlined" size="small"
          onChange={(e) => setBrandId(e.target.value)}
        >
          <MenuItem value=""><em>Select a brand…</em></MenuItem>
          {[...brands].sort((a, b) => (a.brand || "").localeCompare(b.brand || "")).map((b) => (
            <MenuItem key={b.brand_id} value={b.brand_id}>{b.brand}</MenuItem>
          ))}
        </TextField>

        <TextField
          select label="Status (blank = all)" value={statuses} style={{ width: 260 }} variant="outlined" size="small"
          SelectProps={{ multiple: true, renderValue: (sel) =>
            sel.length === 0 ? "All" :
            sel.map((id) => statusList.find((s) => String(s.status_id) === String(id))?.status || id).join(", ") }}
          onChange={(e) => setStatuses(e.target.value)}
        >
          {statusList.map((s) => (
            <MenuItem key={s.status_id} value={s.status_id}>
              <Checkbox checked={statuses.map(String).includes(String(s.status_id))} />
              {s.status}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          label="Created since (optional)" type="date" value={createdSince} style={{ width: 220 }}
          variant="outlined" size="small" InputLabelProps={{ shrink: true }}
          onChange={(e) => setCreatedSince(e.target.value)}
        />
      </Box>

      <Box mt={2} mb={3}>
        <Typography variant="body2" style={{ fontWeight: 600 }}>
          {brandId ? `${count ?? "…"} item(s) match` : "Select a brand to preview"}
        </Typography>
      </Box>

      <Divider style={{ margin: "8px 0 20px" }} />

      {/* Field 1: Warranty */}
      <Box display="flex" alignItems="center" style={{ gap: 20 }} mb={2}>
        <FormControlLabel
          control={<Switch color="primary" checked={modifyWarranty} onChange={(e) => setModifyWarranty(e.target.checked)} />}
          label="Modify Warranty"
        />
        <TextField
          select label="Warranty" value={warrantyId} style={{ width: 300 }} variant="outlined" size="small"
          disabled={!modifyWarranty} onChange={(e) => setWarrantyId(e.target.value)}
        >
          <MenuItem value=""><em>(none / clear)</em></MenuItem>
          {[...warrantyList]
            .filter((w) => String(w.site_id) === String(siteId))
            .sort((a, b) => (a.warranty || "").localeCompare(b.warranty || "")).map((w) => (
            <MenuItem key={w.warranty_id} value={w.warranty_id}>{w.warranty}</MenuItem>
          ))}
        </TextField>
      </Box>

      {/* Field 2: API (papers included) */}
      <Box display="flex" alignItems="center" style={{ gap: 20 }} mb={3}>
        <FormControlLabel
          control={<Switch color="primary" checked={modifyPapers} onChange={(e) => setModifyPapers(e.target.checked)} />}
          label="Modify API"
        />
        <FormControlLabel
          control={<Checkbox checked={papersIncluded} disabled={!modifyPapers}
            onChange={(e) => setPapersIncluded(e.target.checked)} />}
          label="Papers Included (API)"
        />
      </Box>

      <Button
        variant="contained" color="primary" disabled={!canGo || loading}
        onClick={() => setConfirmOpen(true)}
      >
        {loading ? <CircularProgress size={22} /> : "Update"}
      </Button>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm mass update</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Set <b>{changesSummary() || "(nothing selected)"}</b><br />
            for <b>{count ?? "…"}</b> item(s) in <b>{brandName}</b> on <b>{siteLabel(siteId)}</b>
            {statuses.length ? ` (statuses: ${statuses.map((id) => statusList.find((s) => String(s.status_id) === String(id))?.status || id).join(", ")})` : " (all statuses)"}
            {createdSince ? `, created since ${createdSince}` : ""}.
          </Typography>
          <Typography variant="body2" color="error" style={{ marginTop: 12 }}>
            This cannot be undone. Proceed?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
          <Button color="primary" variant="contained" onClick={doUpdate}>Yes, update</Button>
        </DialogActions>
      </Dialog>

      {results.length > 0 && (
        <Box mt={4} component={Paper} variant="outlined" p={2}>
          <Typography variant="h6" gutterBottom>Results</Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Time</TableCell><TableCell>Brand</TableCell>
                <TableCell>Site</TableCell><TableCell>Items updated</TableCell><TableCell>Changes</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {results.map((r, i) => (
                <TableRow key={i}>
                  <TableCell>{r.time}</TableCell>
                  <TableCell>{r.brand}</TableCell>
                  <TableCell>{siteLabel(r.site)}</TableCell>
                  <TableCell>{r.count}</TableCell>
                  <TableCell>
                    {r.changes.warranty_id !== undefined ? `warranty_id=${r.changes.warranty_id ?? "null"} ` : ""}
                    {r.changes.papers_included !== undefined ? `papers_included=${r.changes.papers_included}` : ""}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export const MassUpdatePage = React.memo(_MassUpdatePage);
export default MassUpdatePage;
